import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Check, Clock, MapPin, Phone, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { getAttribution } from "@/lib/attribution";
import { submitEnquiry } from "@/lib/enquiries.functions";
import { FEES, FIRM, FIRM_ADDRESS, NO_CHARGE_GUARANTEE, PENINSULA_ADDRESS, PEOPLE } from "@/lib/firm";
import { HEARD_ABOUT_OPTIONS } from "@/components/enquiry-form";
import { GOOGLE_REVIEWS, GOOGLE_REVIEW_URL } from "@/lib/google-reviews";
import { Button } from "@/components/ui/button";

const field =
  "w-full rounded-sm border border-input bg-background px-3 py-3 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";

/** Short, high-converting quote form for ad landing pages (4 questions). */
export function QuickQuoteForm({ suburb, heading, id = "enquiry" }: { suburb: string; heading?: string; id?: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [matter, setMatter] = useState<"conveyancing-purchase" | "conveyancing-sale">("conveyancing-purchase");
  const [errors, setErrors] = useState<{ name?: string; phone?: string; email?: string }>({});

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const fd = new FormData(event.currentTarget);
    const name = String(fd.get("name") ?? "").trim();
    const phone = String(fd.get("phone") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    const contract = fd.get("contract") === "on";
    const next: typeof errors = {};
    if (name.length < 2) next.name = "Please tell us your name";
    if (phone.replace(/\D/g, "").length < 8) next.phone = "Please enter a phone number";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) next.email = "Please enter a valid email";
    setErrors(next);
    if (Object.keys(next).length) {
      const firstInvalid = next.name ? "name" : next.phone ? "phone" : "email";
      event.currentTarget.querySelector<HTMLInputElement>(`[name="${firstInvalid}"]`)?.focus();
      return;
    }

    const action = matter === "conveyancing-sale" ? "selling" : "buying";
    setSending(true);
    try {
      await submitEnquiry({
        data: {
          name,
          email,
          phone,
          matterType: matter,
          suburb,
          propertyAddress: "",
          keyDate: "",
          heardAbout: String(fd.get("heardAbout") ?? "").slice(0, 100),
          message: `Quick quote request from the ${suburb} page: ${action} a property in ${suburb}.${
            contract ? " Has a contract and would like the free contract review — please send a secure way to share it." : ""
          }`,
          ...getAttribution(pathname),
        } as never,
      });
      void import("@/lib/google-ads").then((m) => m.reportEnquiryConversion());
      void navigate({ to: "/thank-you", search: { type: "enquiry", matter } });
    } catch {
      toast.error(`Something went wrong. Please call us on ${FIRM.phone}.`);
    } finally {
      setSending(false);
    }
  }

  return (
    <form
      id={id}
      onSubmit={onSubmit}
      noValidate
      aria-busy={sending}
      className="scroll-mt-24 rounded-md border border-border bg-card p-5 shadow-xl sm:p-7"
    >
      <p className="font-serif text-2xl leading-tight text-foreground">
        {heading ?? `Get your fixed ${suburb} quote`}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">Takes 30 seconds. We reply the same business day.</p>

      <fieldset className="mt-5">
        <legend className="sr-only">Are you buying or selling?</legend>
        <div className="grid grid-cols-2 gap-2">
          {([
            ["conveyancing-purchase", "I'm buying", FEES.purchase],
            ["conveyancing-sale", "I'm selling", FEES.sale],
          ] as const).map(([value, label, fee]) => (
            <Button
              key={value}
              type="button"
              variant="outline"
              aria-pressed={matter === value}
              onClick={() => setMatter(value)}
              className={`block h-auto whitespace-normal rounded-sm border px-3 py-3 text-left text-sm transition-colors ${
                matter === value
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-input bg-background text-muted-foreground hover:border-primary/60"
              }`}
            >
              <span className="block font-semibold text-foreground">{label}</span>
              <span className="text-xs">{fee} fixed, inc. GST</span>
            </Button>
          ))}
        </div>
      </fieldset>

      <div className="mt-4 space-y-3">
        <p role="alert" className="sr-only">
          {Object.keys(errors).length > 0 ? "Please check your contact details below." : ""}
        </p>
        <div>
          <label htmlFor={`${id}-qq-name`} className="sr-only">Your name</label>
          <input id={`${id}-qq-name`} name="name" placeholder="Your name" autoComplete="name" className={field} required aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? `${id}-qq-name-error` : undefined} />
          {errors.name && <p id={`${id}-qq-name-error`} className="mt-1 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor={`${id}-qq-phone`} className="sr-only">Phone</label>
          <input id={`${id}-qq-phone`} name="phone" type="tel" placeholder="Mobile number" autoComplete="tel" inputMode="tel" className={field} required aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? `${id}-qq-phone-error` : undefined} />
          {errors.phone && <p id={`${id}-qq-phone-error`} className="mt-1 text-xs text-destructive">{errors.phone}</p>}
        </div>
        <div>
          <label htmlFor={`${id}-qq-email`} className="sr-only">Email</label>
          <input id={`${id}-qq-email`} name="email" type="email" placeholder="Email" autoComplete="email" className={field} required aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? `${id}-qq-email-error` : undefined} />
          {errors.email && <p id={`${id}-qq-email-error`} className="mt-1 text-xs text-destructive">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor={`${id}-qq-heard`} className="sr-only">How did you hear about us?</label>
          <select id={`${id}-qq-heard`} name="heardAbout" className={field} defaultValue="">
            <option value="">How did you hear about us?</option>
            {HEARD_ABOUT_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <label className="flex items-start gap-2 text-base text-muted-foreground">
          <input type="checkbox" name="contract" className="mt-1 size-4" />
          <span>I have a contract and want the <strong className="text-foreground">free contract review</strong></span>
        </label>
      </div>

      <Button
        type="submit"
        disabled={sending}
        className="mt-5 h-auto w-full whitespace-normal rounded-sm px-6 py-3.5 text-base font-semibold"
      >
        {sending ? "Sending…" : "Get my fixed quote"}
      </Button>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        No obligation. Or call <a href={FIRM.phoneHref} className="font-semibold text-primary underline">{FIRM.phone}</a>
      </p>
    </form>
  );
}

function Stars() {
  return <span className="text-primary" aria-hidden>★★★★★</span>;
}

export function AdLandingHero({
  eyebrow,
  title,
  lead,
  suburb,
  personSlug = "erin-wilkinson",
}: {
  eyebrow: string;
  title: string;
  lead: string;
  suburb: string;
  personSlug?: string;
}) {
  const person = PEOPLE.find((p) => p.slug === personSlug);
  const firstName = person?.name.split(" ")[0] ?? "us";
  return (
    <section className="border-b border-border bg-gradient-to-br from-secondary/70 via-background to-secondary/40">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-8 sm:py-14 lg:grid-cols-[1.15fr_1fr] lg:items-start">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-foreground/70">{eyebrow}</p>
          <h1 className="mt-3 font-serif text-[2rem] leading-tight text-foreground sm:text-5xl">{title}</h1>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm shadow-sm"
          >
            <Stars /> <span className="font-semibold text-foreground">5.0</span>
            <span className="text-muted-foreground">from {GOOGLE_REVIEWS.length} Google reviews</span>
          </a>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">{lead}</p>
          <ul className="mt-5 space-y-2 text-base text-foreground">
            {[
              `Fixed fee: ${FEES.sale} sale · ${FEES.purchase} purchase, inc. GST`,
              "Free contract review before you sign",
              "No Charge Guarantee if your contract falls over",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2">
                <Check className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden /> {t}
              </li>
            ))}
          </ul>
          {person && (
            <div className="mt-6 flex items-center gap-4 rounded-md border border-border bg-card p-3 shadow-sm">
              <img
                src={person.photo}
                alt={`${person.name}, ${person.role} at ${FIRM.name}`}
                width={72}
                height={72}
                loading="eager"
                className="size-[72px] shrink-0 rounded-full object-cover object-top"
              />
              <div className="text-sm">
                <p className="font-semibold text-foreground">Your matter is handled by {person.name}</p>
                <p className="text-muted-foreground">{person.role} · lives in {person.based.split(",")[0]}</p>
                <a href={FIRM.phoneHref} className="mt-1 inline-flex items-center gap-1 font-semibold text-primary">
                  <Phone className="size-4" aria-hidden /> Call {firstName}: {FIRM.phone}
                </a>
              </div>
            </div>
          )}
          <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="size-3.5" aria-hidden /> Mon–Fri 8:30am–5pm · after-hours by arrangement
          </p>
        </div>
        <QuickQuoteForm suburb={suburb} />
      </div>
    </section>
  );
}

export function ReviewCards({ count = 3 }: { count?: number }) {
  const picks = GOOGLE_REVIEWS.filter((r) => /erin|settlement|purchas|home|property/i.test(r.body)).slice(0, count);
  return (
    <section className="mx-auto max-w-6xl px-5 py-12">
      <h2 className="font-serif text-3xl text-foreground">What local clients say</h2>
      <p className="mt-1 text-sm text-muted-foreground">Verbatim from our Google Business Profile.</p>
      <ul className="mt-6 grid gap-4 md:grid-cols-3">
        {picks.map((r) => (
          <li key={r.id} className="[&:nth-child(3)]:hidden md:[&:nth-child(3)]:block rounded-md border border-border bg-card p-5 shadow-sm">
            <Stars />
            <p className="mt-3 line-clamp-6 text-base leading-relaxed text-muted-foreground">“{r.body}”</p>
            <p className="mt-3 text-sm font-semibold text-foreground">
              {r.name} <span className="font-normal text-muted-foreground">· Google review</span>
            </p>
          </li>
        ))}
      </ul>
      <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-sm font-semibold text-primary underline">
        Read all our Google reviews
      </a>
    </section>
  );
}

export function WhatsIncluded() {
  const items = [
    "Solicitor review of your contract, with advice before and after you sign",
    "Managing finance, building and pest and other condition dates",
    "Ordering and reviewing property searches",
    "Liaising with the agent, your lender and the other side",
    "Transfer documents and stamp duty paperwork (buyers)",
    "Electronic settlement through PEXA and settlement-day updates",
  ];
  return (
    <section className="border-y border-border bg-secondary/40">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 md:grid-cols-2">
        <div>
          <h2 className="font-serif text-3xl text-foreground">What's included in your fixed fee</h2>
          <ul className="mt-5 space-y-2.5">
            {items.map((t) => (
              <li key={t} className="flex items-start gap-2 text-base text-foreground">
                <Check className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden /> {t}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            No hidden extras. Searches, registration and government fees are passed on at cost and itemised in writing before you commit.
          </p>
        </div>
        <div className="rounded-md border border-primary/30 bg-card p-6">
          <p className="flex items-center gap-2 font-serif text-2xl text-foreground">
            <ShieldCheck className="size-6 text-primary" aria-hidden /> {NO_CHARGE_GUARANTEE.title}
          </p>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">{NO_CHARGE_GUARANTEE.short}</p>
          <ul className="mt-4 space-y-2">
            {NO_CHARGE_GUARANTEE.points.map((p) => (
              <li key={p} className="flex items-start gap-2 text-base text-foreground">
                <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden /> {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function SettlementSteps() {
  const steps = [
    { t: "Send us the contract", d: "Before or straight after you sign. We check it and confirm your fixed fee in writing." },
    { t: "Searches and conditions", d: "We order searches and keep finance and building and pest dates on track." },
    { t: "Finance and paperwork", d: "We work with your lender or the buyer's, and prepare transfer documents." },
    { t: "Settlement day", d: "We settle electronically through PEXA and call you when it's done." },
  ];
  return (
    <section className="mx-auto max-w-6xl px-5 py-12">
      <h2 className="font-serif text-3xl text-foreground">How it works — 4 simple steps</h2>
      <ol className="mt-6 grid gap-3 md:grid-cols-4 md:gap-4">
        {steps.map((s, i) => (
          <li key={s.t} className="flex gap-4 rounded-md border border-border bg-card p-4 md:block md:p-5">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground">
              {i + 1}
            </span>
            <div>
              <p className="font-serif text-lg text-foreground md:mt-3">{s.t}</p>
              <p className="mt-1 text-base leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function MidCta({ suburb }: { suburb: string }) {
  return (
    <div className="mx-auto max-w-6xl px-5">
      <div className="flex flex-col items-start justify-between gap-4 rounded-md bg-primary px-6 py-6 text-primary-foreground sm:flex-row sm:items-center">
        <p className="font-serif text-xl sm:text-2xl">Get your fixed {suburb} quote today</p>
        <div className="flex flex-wrap gap-2">
          <a href="#enquiry" className="rounded-sm bg-background px-5 py-3 text-sm font-semibold text-foreground">
            Get my quote
          </a>
          <a href={FIRM.phoneHref} className="rounded-sm border border-primary-foreground/60 px-5 py-3 text-sm font-semibold">
            Call {FIRM.phone}
          </a>
        </div>
      </div>
    </div>
  );
}

export function TrustAndOffices() {
  return (
    <section className="mx-auto grid max-w-6xl gap-6 px-5 py-12 md:grid-cols-2">
      <div>
        <h2 className="font-serif text-3xl text-foreground">Real local solicitors</h2>
        <ul className="mt-5 space-y-2.5 text-base text-foreground">
          <li className="flex items-start gap-2"><Check className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden /> Practising solicitors, members of the Queensland Law Society</li>
          <li className="flex items-start gap-2"><Check className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden /> Erin: years as a South East Queensland conveyancer before admission</li>
          <li className="flex items-start gap-2"><Check className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden /> Michael: practising since 2003, former managing partner of GKS Law</li>
        </ul>
        <div className="mt-6 space-y-3 text-base">
          <p className="flex items-start gap-2 text-muted-foreground"><MapPin className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden /> <span><strong className="text-foreground">Sunshine Coast:</strong> {FIRM_ADDRESS}</span></p>
          <p className="flex items-start gap-2 text-muted-foreground"><MapPin className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden /> <span><strong className="text-foreground">Moreton Bay:</strong> {PENINSULA_ADDRESS} (by appointment)</span></p>
        </div>
      </div>
      <iframe
        title="Coastside Law, Golden Beach office map"
        src={`https://www.google.com/maps?q=${encodeURIComponent(`Coastside Law, ${FIRM_ADDRESS}`)}&output=embed`}
        loading="lazy"
        className="h-72 w-full rounded-md border border-border"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </section>
  );
}
